import { useAccount } from '@/account/useAccount';
import { SearchFormPlacements } from '@/components/SearchFormsContainer/interfaces';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import { RecentReportsSuggestions } from '@/reports/shared/RecentReportsSuggestions/RecentReportsSuggestions';
import { FcraWarning } from '@/reports/shared/searchFormsComponents/components';
import { useReportRedirect } from '@/reports/useReportRedirect/useReportRedirect';
import { RapidsInput } from '@/search/index';
import { RapidsSearchData } from '@/search/rapids/rapids';
import { ListingsSearchParams } from '@/search/useSearchListings/useSearchListings';
import { Icons, Stack, Text, useTheme, useMediaQuery } from '@/theme/index';

/**
 * Form used to search 'Pre-foreclosure' properties.
 * After searching, the user is redirected to the listings search results page.
 */
export function SearchPFCPropertiesForm({
  closeForm,
  placement = 'header',
}: {
  /**
   * Callback fired when user clicks 'Search' button or selects
   * an address from the autocomplete suggestions.
   * This callback will close all open tabs/forms in the header.
   */
  closeForm: () => void;
} & SearchFormPlacements) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { redirectToSearchPropertyListingsUrl } = useReportRedirect();
  const { data: account } = useAccount(true);
  const {
    links: { tosLink, fcraLink },
    data: { brandName },
  } = useAppConstantsContext();
  const { trackEventGA4 } = useAppConfigContext();

  function getProperty(propertyData: RapidsSearchData) {
    const userInRecycling = account?.account.user_info.user_in_recycling;
    const hasRecyclingReports =
      !!account?.account.subscription_info.recycling_reports_remaining;

    if (userInRecycling && !hasRecyclingReports) {
      window.dispatchEvent(new CustomEvent('openRecyclingModal'));
      return;
    }

    const { city, state, zipcode } = propertyData;
    if (!city && !state && !zipcode) {
      return;
    }

    const personByAddressSearchParams: ListingsSearchParams = {};
    if (city && state) {
      personByAddressSearchParams.city = city;
      personByAddressSearchParams.state = state;
    }

    if (zipcode) {
      personByAddressSearchParams.zip5 = zipcode;
    }

    personByAddressSearchParams.search_type = 'pre_foreclosures';

    trackEventGA4({
      event_name: 'report_search',
      report_type: 'property',
      search_by: 'pre_foreclosure',
      search_from: `${placement}_search`,
    });
    redirectToSearchPropertyListingsUrl(personByAddressSearchParams);
    closeForm();
  }

  const customStyles = {
    padding: theme.spacing(0, 2),

    '.MuiButton-root': {
      margin: isMobile ? theme.spacing(2, 0, 0, 0) : theme.spacing(0, 0, 0, 2),
    },
  };

  const isInHeader = placement === 'header';

  return (
    <form id="search-pre-foreclosure-properties">
      <RapidsInput
        parentFormSubmitHandler={getProperty}
        inputOptions={{
          customStyles,
          placeholder: 'Enter an address, city, or zip code',
          hideInputLabel: placement === 'dashboard',
        }}
      />
      <Stack px={2}>
        <Text variant="body2" marginBottom={1}>
          <Icons.Lightbulb
            fontSize="small"
            color="primary"
            sx={{ verticalAlign: 'bottom', mr: 0.5 }}
          />
          See properties going into foreclosure by city, state or zip.
        </Text>
        {isInHeader && (
          <RecentReportsSuggestions
            reportType="property_report"
            closeForm={closeForm}
          />
        )}
        <FcraWarning
          fcraLink={fcraLink}
          tosLink={tosLink}
          brandName={brandName}
        />
      </Stack>
    </form>
  );
}
